// Confluence REST API v2 service — OAuth2 + Offline-First
//
// All API responses are cached in IndexedDB so the app continues to work
// when offline. Cached data is served immediately; background refresh
// happens when online.
//
// API docs: https://developer.atlassian.com/cloud/confluence/rest/v2/intro/

import { atlassianOAuth2 } from "./atlassian-oauth2";
import { getSetting, setSetting } from "./storage";
import type { ConfluencePage, ConfluenceSpace, ConfluenceActivity } from "./types";

// ---------------------------------------------------------------------------
// Cache keys
// ---------------------------------------------------------------------------

const CACHE_KEY_SPACES = "confluence-cache-spaces";
const CACHE_KEY_PAGES = (spaceKey: string) => `confluence-cache-pages-${spaceKey}`;
const CACHE_KEY_ACTIVITY = "confluence-cache-activity";
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface CacheEntry<T> {
  data: T;
  cachedAt: number;
  cloudId: string;
}

// ---------------------------------------------------------------------------
// ConfluenceService
// ---------------------------------------------------------------------------

export class ConfluenceService {
  private cloudId: string;
  private siteUrl: string; // e.g. "deiterate.atlassian.net"
  private baseUrl: string;

  constructor(cloudId: string, siteUrl?: string) {
    this.cloudId = cloudId;
    this.siteUrl = siteUrl || `${cloudId}.atlassian.net`;
    this.baseUrl = `https://api.atlassian.com/ex/confluence/${cloudId}/wiki/api/v2`;
  }

  // ---------------------------------------------------------------------------
  // HTTP helper — attaches OAuth2 Bearer and handles offline gracefully
  // ---------------------------------------------------------------------------

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const accessToken = await atlassianOAuth2.getAccessToken();
    if (!accessToken) {
      throw new Error("Not authenticated with Atlassian — please connect via Settings");
    }

    const url = `${this.baseUrl}${path}`;
    console.log(`[Confluence] ${options.method ?? "GET"} ${url}`);

    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
        ...((options.headers as Record<string, string>) ?? {}),
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      console.error(`[Confluence] ${response.status} ${url}: ${text.slice(0, 200)}`);
      throw new Error(`Confluence API error ${response.status}: ${text || response.statusText}`);
    }

    return response.json() as Promise<T>;
  }

  // ---------------------------------------------------------------------------
  // Cache helpers
  // ---------------------------------------------------------------------------

  private async getCached<T>(key: string): Promise<T | null> {
    const entry = await getSetting<CacheEntry<T>>(key);
    if (!entry) return null;
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) return null;
    if (entry.cloudId !== this.cloudId) return null;
    return entry.data;
  }

  private async setCached<T>(key: string, data: T): Promise<void> {
    await setSetting<CacheEntry<T>>(key, { data, cachedAt: Date.now(), cloudId: this.cloudId });
  }

  /**
   * Offline-first fetch: return cached data immediately, refresh in background if online.
   * If no cache and offline: throws with a clear offline message.
   */
  private async fetchWithCache<T>(
    cacheKey: string,
    fetchFn: () => Promise<T>
  ): Promise<{ data: T; fromCache: boolean }> {
    const cached = await this.getCached<T>(cacheKey);

    if (cached && !navigator.onLine) {
      console.log(`[Confluence] Offline — serving cached data for key: ${cacheKey}`);
      return { data: cached, fromCache: true };
    }

    if (cached && navigator.onLine) {
      // Return cache immediately, refresh asynchronously
      fetchFn()
        .then((fresh) => this.setCached(cacheKey, fresh))
        .catch((e) => console.warn(`[Confluence] Background refresh failed (${cacheKey}):`, e));
      return { data: cached, fromCache: true };
    }

    if (!navigator.onLine) {
      throw new Error(
        "No Confluence data cached yet. Connect to the internet to load data for the first time, then it will be available offline."
      );
    }

    // Online, no cache — fetch fresh
    const data = await fetchFn();
    await this.setCached(cacheKey, data);
    return { data, fromCache: false };
  }

  // ---------------------------------------------------------------------------
  // Spaces
  // ---------------------------------------------------------------------------

  async getSpaces(): Promise<{ spaces: ConfluenceSpace[]; fromCache: boolean }> {
    const { data, fromCache } = await this.fetchWithCache<ConfluenceSpace[]>(
      CACHE_KEY_SPACES,
      async () => {
        type SpaceResult = {
          id: string; key: string; name: string; type: string; status: string;
          spaceOwnerId?: string;
          description?: { plain?: { value?: string } };
          _links?: { webui?: string };
        };
        type SpacePage = { results: SpaceResult[]; _links?: { next?: string } };

        const allResults: SpaceResult[] = [];
        let path = "/spaces?limit=250";

        while (path) {
          const resp = await this.request<SpacePage>(path);
          allResults.push(...resp.results);
          // _links.next is a full wiki path like /wiki/api/v2/spaces?cursor=...
          // Strip the /wiki prefix since our baseUrl already ends at /wiki/api/v2
          const next = resp._links?.next;
          if (next) {
            path = next.replace(/^\/wiki\/api\/v2/, "");
          } else {
            break;
          }
        }

        return allResults.map((s) => ({
          id: s.id,
          key: s.key,
          name: s.name,
          type: (s.type ?? "global") as ConfluenceSpace["type"],
          status: (s.status ?? "current") as ConfluenceSpace["status"],
          description: s.description?.plain?.value,
          spaceOwnerId: s.spaceOwnerId,
          url: s._links?.webui
            ? `https://${this.siteUrl}/wiki${s._links.webui}`
            : `https://${this.siteUrl}/wiki/spaces/${s.key}`,
        }));
      }
    );

    return { spaces: data, fromCache };
  }

  // ---------------------------------------------------------------------------
  // Pages
  // ---------------------------------------------------------------------------

  async getPagesInSpace(
    spaceKey: string,
    limit = 100
  ): Promise<{ pages: ConfluencePage[]; fromCache: boolean }> {
    const { data, fromCache } = await this.fetchWithCache<ConfluencePage[]>(
      CACHE_KEY_PAGES(spaceKey),
      async () => {
        // First, resolve spaceKey to spaceId via the v2 spaces endpoint
        const spacesResp = await this.request<{
          results: Array<{ id: string; key: string; name: string }>;
        }>(`/spaces?keys=${encodeURIComponent(spaceKey)}&limit=1`);

        if (!spacesResp.results.length) {
          throw new Error(`Space not found: ${spaceKey}`);
        }
        const spaceId = spacesResp.results[0].id;
        const spaceName = spacesResp.results[0].name;

        const resp = await this.request<{
          results: Array<{
            id: string;
            title: string;
            status: string;
            version: { number: number; createdAt: string };
            authorId?: string;
            _links?: { webui?: string };
          }>;
        }>(`/pages?spaceId=${spaceId}&limit=${limit}&sort=-modified-date&body-format=atlas_doc_format`);

        return resp.results.map((p) => ({
          id: p.id,
          title: p.title,
          spaceKey,
          spaceName,
          status: (p.status ?? "current") as ConfluencePage["status"],
          authorId: p.authorId ?? "",
          createdAt: p.version?.createdAt ?? new Date().toISOString(),
          updatedAt: p.version?.createdAt ?? new Date().toISOString(),
          version: p.version?.number ?? 1,
          url: p._links?.webui
            ? `https://${this.siteUrl}/wiki${p._links.webui}`
            : `https://${this.siteUrl}/wiki/spaces/${spaceKey}/pages/${p.id}`,
        }));
      }
    );

    return { pages: data, fromCache };
  }

  // ---------------------------------------------------------------------------
  // Recent Activity
  // ---------------------------------------------------------------------------

  async getRecentActivity(limit = 50): Promise<{ activity: ConfluenceActivity[]; fromCache: boolean }> {
    const { data, fromCache } = await this.fetchWithCache<ConfluenceActivity[]>(
      CACHE_KEY_ACTIVITY,
      async () => {
        // Use v1 API for activity (v2 doesn't have a direct activity endpoint yet)
        const v1Base = `https://api.atlassian.com/ex/confluence/${this.cloudId}/wiki/rest/api`;
        const accessToken = await atlassianOAuth2.getAccessToken();

        const response = await fetch(
          `${v1Base}/audit?limit=${limit}&startDate=${new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          console.warn("[Confluence] Activity endpoint returned:", response.status);
          return [];
        }

        const data = await response.json();
        const records = data.results ?? [];

        return records
          .filter((r: Record<string, unknown>) => r.objectType === "page" || r.type === "page_created" || r.type === "page_updated")
          .slice(0, limit)
          .map((r: Record<string, unknown>) => {
            const obj = r.objectMeta as Record<string, unknown> | undefined;
            const author = r.author as Record<string, unknown> | undefined;
            return {
              id: String(r.id ?? Math.random()),
              timestamp: String(r.creationDate ?? new Date().toISOString()),
              type: "page_updated" as ConfluenceActivity["type"],
              pageId: String(obj?.id ?? ""),
              pageTitle: String(obj?.display ?? "Unknown"),
              spaceKey: String(obj?.conf_space_key ?? ""),
              authorId: String(author?.accountId ?? ""),
              authorDisplayName: String(author?.displayName ?? ""),
            };
          });
      }
    );

    return { activity: data, fromCache };
  }

  // ---------------------------------------------------------------------------
  // Pages in date range (for activity view)
  // ---------------------------------------------------------------------------

  async getPagesModifiedInRange(
    spaceKey: string,
    from: string,
    to: string,
    limit = 200
  ): Promise<{ pages: ConfluencePage[]; fromCache: boolean }> {
    const cacheKey = `confluence-cache-range-${spaceKey}-${from}-${to}`;
    const { data, fromCache } = await this.fetchWithCache<ConfluencePage[]>(
      cacheKey,
      async () => {
        const spacesResp = await this.request<{
          results: Array<{ id: string; key: string; name: string }>;
        }>(`/spaces?keys=${encodeURIComponent(spaceKey)}&limit=1`);
        if (!spacesResp.results.length) throw new Error(`Space not found: ${spaceKey}`);
        const spaceId = spacesResp.results[0].id;
        const spaceName = spacesResp.results[0].name;

        const resp = await this.request<{
          results: Array<{
            id: string;
            title: string;
            status: string;
            version: { number: number; createdAt: string; authorId?: string; message?: string };
            authorId?: string;
            createdAt?: string;
            _links?: { webui?: string };
          }>;
        }>(`/pages?spaceId=${spaceId}&limit=${limit}&sort=-modified-date`);

        const fromTs = new Date(from).getTime();
        const toTs = new Date(to).getTime() + 86_400_000; // inclusive end day

        return resp.results
          .filter((p) => {
            const ts = new Date(p.version?.createdAt ?? 0).getTime();
            return ts >= fromTs && ts <= toTs;
          })
          .map((p) => ({
            id: p.id,
            title: p.title,
            spaceKey,
            spaceName,
            status: (p.status ?? "current") as ConfluencePage["status"],
            authorId: p.version?.authorId ?? p.authorId ?? "",
            createdAt: p.createdAt ?? p.version?.createdAt ?? new Date().toISOString(),
            updatedAt: p.version?.createdAt ?? new Date().toISOString(),
            version: p.version?.number ?? 1,
            url: p._links?.webui
              ? `https://${this.siteUrl}/wiki${p._links.webui}`
              : `https://${this.siteUrl}/wiki/spaces/${spaceKey}/pages/${p.id}`,
          }));
      }
    );
    return { pages: data, fromCache };
  }

  // ---------------------------------------------------------------------------
  // Fetch pages for a specific space OR all spaces (__ALL__)
  // Handles deduplication. Used by CombinedProjectView and presets.
  // ---------------------------------------------------------------------------

  async getPagesForSpaceOrAll(
    spaceKey: string,
    from: string,
    to: string
  ): Promise<ConfluencePage[]> {
    let allPages: ConfluencePage[] = [];

    if (spaceKey === '__ALL__') {
      const { spaces } = await this.getSpaces();
      const current = spaces.filter(s => s.status === 'current');
      const results = await Promise.allSettled(
        current.map(s => this.getPagesModifiedInRange(s.key, from, to))
      );
      for (const r of results) {
        if (r.status === 'fulfilled') allPages = allPages.concat(r.value.pages);
      }
    } else {
      const { pages } = await this.getPagesModifiedInRange(spaceKey, from, to);
      allPages = pages;
    }

    // Deduplicate by page ID
    const seen = new Set<string>();
    return allPages.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }

  // ---------------------------------------------------------------------------
  // Get page with full content for detail view
  // ---------------------------------------------------------------------------

  async getPageWithContent(pageId: string, version?: number): Promise<any> {
    let pageResp: any;
    
    if (version && typeof version === 'number' && version > 0 && Number.isInteger(version)) {
      // v2 list endpoint with body-format returns page.body for each version
      // Single version endpoint only returns metadata - no content
      try {
        const versionsData = await this.request<{
          results: Array<{
            number: number;
            authorId: string;
            createdAt: string;
            message?: string;
            minorEdit?: boolean;
            page?: {
              id: string;
              title: string;
              status: string;
              spaceId?: string;
              _links?: { webui?: string };
              body?: {
                atlas_doc_format?: { value: string; representation: string };
                storage?: { value: string; representation: string };
              };
            };
          }>;
        }>(`/pages/${pageId}/versions?body-format=atlas_doc_format&limit=250`);

        const versionEntry = versionsData.results.find(v => v.number === version);
        if (!versionEntry) throw new Error(`Version ${version} not found`);

        const page = versionEntry.page ?? { id: undefined as string | undefined, title: undefined as string | undefined, status: undefined as string | undefined, spaceId: undefined as string | undefined, _links: undefined as { webui?: string } | undefined, body: undefined as { atlas_doc_format?: { value: string; representation: string }; storage?: { value: string; representation: string } } | undefined };
        pageResp = {
          id: page.id || pageId,
          title: page.title,
          status: page.status,
          spaceId: page.spaceId,
          _links: page._links,
          author: { accountId: versionEntry.authorId },
          createdAt: versionEntry.createdAt,
          modifiedAt: versionEntry.createdAt,
          version: { number: versionEntry.number, when: versionEntry.createdAt, author: { accountId: versionEntry.authorId }, message: versionEntry.message },
          body: page.body,
        };
      } catch (e) {
        // If version fetch fails, fall back to current content
        console.warn('Could not fetch version content, falling back to current:', e);
        pageResp = await this.request<{
          id: string;
          title: string;
          status: string;
          version: { number: number; when: string; author?: any; message?: string };
          author?: any;
          createdAt: string;
          modifiedAt: string;
          spaceId: string;
          _links?: { webui?: string };
          body?: {
            atlas_doc_format?: {
              value: string;
              representation: string;
            };
            storage?: {
              value: string;
              representation: string;
            };
          };
        }>(`/pages/${pageId}?body-format=atlas_doc_format`);
      }
    } else {
      // Get current page info with ADF content
      pageResp = await this.request<{
        id: string;
        title: string;
        status: string;
        version: { number: number; when: string; author?: any; message?: string };
        author?: any;
        createdAt: string;
        modifiedAt: string;
        spaceId: string;
        _links?: { webui?: string };
        body?: {
          atlas_doc_format?: {
            value: string;
            representation: string;
          };
          storage?: {
            value: string;
            representation: string;
          };
        };
      }>(`/pages/${pageId}?body-format=atlas_doc_format`);
    }

    // Get space info
    let spaceResp: { id: string; key: string; name: string } | null = null;
    if (pageResp.spaceId) {
      spaceResp = await this.request<{ id: string; key: string; name: string }>(`/spaces/${pageResp.spaceId}`);
    } else {
      console.warn('Page has no spaceId, skipping space info fetch');
    }

    // Get attachments
    let attachments: any[] = [];
    try {
      const attResp = await this.request<{
        results: Array<{
          id: string;
          title: string;
          mediaType: string;
          size: number;
          _links: { download: string };
        }>;
      }>(`/pages/${pageId}/attachments?limit=100`);
      attachments = attResp.results.map(att => ({
        id: att.id,
        filename: att.title,
        mimeType: att.mediaType,
        size: att.size,
        downloadUrl: att._links.download,
      }));
    } catch (e) {
      // Attachments might not be accessible
    }

    // Get labels
    let labels: any[] = [];
    try {
      const labelResp = await this.request<{
        results: Array<{
          id: string;
          name: string;
          prefix: string;
        }>;
      }>(`/pages/${pageId}/labels?limit=50`);
      labels = labelResp.results;
    } catch (e) {
      // Labels might not be accessible
    }

    // Get child pages
    let childPages: any[] = [];
    try {
      const childResp = await this.request<{
        results: Array<{
          id: string;
          title: string;
          status: string;
        }>;
      }>(`/pages/${pageId}/children?limit=100`);
      childPages = childResp.results;
    } catch (e) {
      // Children might not be accessible
    }

    const spaceKey = pageResp._spaceKey || spaceResp?.key || 'unknown';
    const spaceName = pageResp._spaceName || spaceResp?.name || 'Unknown Space';
    return {
      id: pageResp.id,
      title: pageResp.title,
      version: pageResp.version,
      author: pageResp.author,
      createdAt: pageResp.createdAt,
      modifiedAt: pageResp.modifiedAt,
      status: pageResp.status,
      spaceKey,
      spaceName,
      spaceId: spaceResp?.id || pageResp.spaceId,
      url: pageResp._links?.webui
        ? `https://${this.siteUrl}/wiki${pageResp._links.webui}`
        : `https://${this.siteUrl}/wiki/spaces/${spaceKey}/pages/${pageResp.id}`,
      content: pageResp.body?.atlas_doc_format ? {
        id: pageResp.id,
        type: 'atlas_doc_format',
        representation: 'atlas_doc_format',
        value: pageResp.body.atlas_doc_format.value,
      } : pageResp.body?.storage ? {
        id: pageResp.id,
        type: 'storage',
        representation: 'storage',
        value: pageResp.body.storage.value,
      } : undefined,
      labels,
      childPages,
      attachments,
    };
  }

  // ---------------------------------------------------------------------------
  // Expand pages into individual version events (for activity feed)
  // ---------------------------------------------------------------------------

  /**
   * Given a list of pages, fetches version history for each and returns one
   * ConfluencePage entry per version that falls within [from, to].
   * Requests are batched (concurrency=20) to avoid rate-limiting.
   */
  async expandPagesToVersionEvents(
    pages: ConfluencePage[],
    from: string,
    to: string
  ): Promise<ConfluencePage[]> {
    const fromTs = new Date(from).getTime();
    const toTs = new Date(to).getTime() + 86_400_000;

    const BATCH = 20;
    const results: ConfluencePage[] = [];

    for (let i = 0; i < pages.length; i += BATCH) {
      const batch = pages.slice(i, i + BATCH);
      const batchResults = await Promise.allSettled(
        batch.map(async (page) => {
          try {
            const resp = await this.request<{
              results: Array<{
                number: number;
                authorId: string;
                createdAt: string;
                message?: string;
                minorEdit?: boolean;
              }>;
              _links?: { next?: string };
            }>(`/pages/${page.id}/versions?limit=250`);

            const versions = resp.results.filter((v) => {
              const ts = new Date(v.createdAt).getTime();
              return ts >= fromTs && ts <= toTs;
            });

            // Strip HTML tags from storage-format comment body
            const storageToText = (html: string): string =>
              html.replace(/<[^>]+>/g, ' ').replace(/\s{2,}/g, ' ').trim();

            // Version entry from /footer-comments/{id}/versions or /inline-comments/{id}/versions
            type CommentVersionEntry = {
              number: number;
              authorId: string;
              createdAt: string;
              message?: string;
              minorEdit?: boolean;
              comment?: { id: string; title?: string; body?: { storage?: { value: string } } };
            };

            // Fetch all comment IDs for a given type, then expand each into version events
            const expandComments = async (
              listPath: string,
              versionsPath: (id: string) => string,
              commentType: 'footer' | 'inline'
            ): Promise<ConfluencePage[]> => {
              // Step 1: get comment list (IDs only, no body needed)
              const listResp = await this.request<{ results: Array<{ id: string }> }>(listPath);
              const commentIds = listResp.results.map(c => c.id);

              // Step 2: per comment, fetch version history and filter by date range
              const perCommentResults = await Promise.allSettled(
                commentIds.map(async (commentId) => {
                  const versResp = await this.request<{ results: CommentVersionEntry[] }>(
                    versionsPath(commentId)
                  );
                  return versResp.results
                    .filter(v => {
                      const ts = new Date(v.createdAt).getTime();
                      return ts >= fromTs && ts <= toTs;
                    })
                    .map(v => ({
                      ...page,
                      commentId,
                      commentType,
                      commentVersion: v.number,
                      commentBody: v.comment?.body?.storage?.value
                        ? storageToText(v.comment.body.storage.value)
                        : '',
                      authorId: v.authorId || page.authorId,
                      updatedAt: v.createdAt,
                      version: v.number,
                      versionMessage: v.message || undefined,
                      minorEdit: v.minorEdit ?? false,
                      eventType: 'comment_added' as const,
                    }));
                })
              );

              const events: ConfluencePage[] = [];
              for (const r of perCommentResults) {
                if (r.status === 'fulfilled') events.push(...r.value);
              }
              return events;
            };

            let commentEvents: ConfluencePage[] = [];
            try {
              const footer = await expandComments(
                `/pages/${page.id}/footer-comments?limit=250`,
                (id) => `/footer-comments/${id}/versions?body-format=storage&limit=250`,
                'footer'
              );
              commentEvents.push(...footer);
            } catch (e) { console.warn('[Confluence] footer-comments failed:', e); }

            try {
              const inline = await expandComments(
                `/pages/${page.id}/inline-comments?limit=250`,
                (id) => `/inline-comments/${id}/versions?body-format=storage&limit=250`,
                'inline'
              );
              commentEvents.push(...inline);
            } catch (e) { console.warn('[Confluence] inline-comments failed:', e); }

            // Each version in the date range → one event
            const versionEvents = versions.map((v) => ({
              ...page,
              authorId: v.authorId || page.authorId,
              updatedAt: v.createdAt,
              version: v.number,
              minorEdit: v.minorEdit ?? false,
              versionMessage: v.message || undefined,
            }));

            return [...versionEvents, ...commentEvents];
          } catch {
            // If version fetch fails, fall back to the page-level event
            return [page];
          }
        })
      );

      for (const r of batchResults) {
        if (r.status === "fulfilled") results.push(...r.value);
      }
    }

    return results;
  }

  // ---------------------------------------------------------------------------
  // Fetch a specific version of a footer or inline comment (for diff)
  // ---------------------------------------------------------------------------

  async getCommentVersionBody(
    commentId: string,
    commentType: 'footer' | 'inline',
    versionNum: number
  ): Promise<string> {
    const prefix = commentType === 'footer' ? 'footer-comments' : 'inline-comments';
    const resp = await this.request<{
      number: number;
      authorId: string;
      createdAt: string;
      comment?: { body?: { storage?: { value: string } } };
    }>(`/${prefix}/${commentId}/versions/${versionNum}?body-format=storage`);
    const raw = resp.comment?.body?.storage?.value ?? '';
    return raw.replace(/<[^>]+>/g, ' ').replace(/\s{2,}/g, ' ').trim();
  }

  // ---------------------------------------------------------------------------
  // Resolve personal space owner display names from Atlassian
  // ---------------------------------------------------------------------------

  async resolvePersonalSpaceNames(spaces: ConfluenceSpace[]): Promise<ConfluenceSpace[]> {
    const personal = spaces.filter(s => s.type === 'personal' && s.spaceOwnerId);
    if (personal.length === 0) return spaces;
    const ownerIds = [...new Set(personal.map(s => s.spaceOwnerId!))];
    const nameMap = await this.resolveUserNames(ownerIds);
    return spaces.map(s => {
      if (s.type !== 'personal' || !s.spaceOwnerId) return s;
      const resolved = nameMap.get(s.spaceOwnerId);
      if (!resolved || resolved === s.spaceOwnerId) return s;
      return { ...s, name: resolved };
    });
  }

  // ---------------------------------------------------------------------------
  // User name resolution (batch, cached)
  // ---------------------------------------------------------------------------

  private userCache: Map<string, string> = new Map();

  async resolveUserNames(accountIds: string[]): Promise<Map<string, string>> {
    const result = new Map<string, string>();
    const toFetch = accountIds.filter((id) => id && !this.userCache.has(id));

    if (toFetch.length > 0) {
      const v1Base = `https://api.atlassian.com/ex/confluence/${this.cloudId}/wiki/rest/api`;
      const accessToken = await atlassianOAuth2.getAccessToken();

      await Promise.allSettled(
        toFetch.map(async (accountId) => {
          try {
            const resp = await fetch(`${v1Base}/user?accountId=${encodeURIComponent(accountId)}`, {
              headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" },
            });
            if (resp.ok) {
              const data = await resp.json();
              this.userCache.set(accountId, data.displayName ?? accountId);
            } else {
              this.userCache.set(accountId, accountId);
            }
          } catch {
            this.userCache.set(accountId, accountId);
          }
        })
      );
    }

    for (const id of accountIds) {
      result.set(id, this.userCache.get(id) ?? id);
    }
    return result;
  }

  // ---------------------------------------------------------------------------
  // Search API (uses search:confluence scope)
  // ---------------------------------------------------------------------------

  /**
   * Search Confluence content across spaces.
   * Requires search:confluence scope.
   */
  async searchContent(query: string, options: { spaceKey?: string; limit?: number } = {}) {
    const params = new URLSearchParams({
      cql: `text ~ "${query}"${options.spaceKey ? ` AND space = ${options.spaceKey}` : ''}`,
      limit: (options.limit ?? 50).toString(),
      expand: 'space,history',
    });
    return this.request<{ results: any[] }>(`/search?${params}`);
  }

  // ---------------------------------------------------------------------------
  // All spaces (cached)
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Cache management
  // ---------------------------------------------------------------------------

  async clearCache(): Promise<void> {
    const spaces = await this.getSpaces().catch(() => ({ spaces: [] }));
    for (const space of spaces.spaces) {
      await setSetting(CACHE_KEY_PAGES(space.key), undefined);
    }
    await setSetting(CACHE_KEY_SPACES, undefined);
    await setSetting(CACHE_KEY_ACTIVITY, undefined);
    console.log("[Confluence] Cache cleared");
  }
}

// ---------------------------------------------------------------------------
// Factory — builds service from stored OAuth2 token
// ---------------------------------------------------------------------------

export async function createConfluenceService(): Promise<ConfluenceService | null> {
  const state = await atlassianOAuth2.getState();
  if (!state.isAuthenticated || !state.token?.cloudId) {
    console.log("[Confluence] Not authenticated or no cloudId — service unavailable");
    return null;
  }
  return new ConfluenceService(state.token.cloudId, state.token.cloudName);
}
